import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import { motion, useInView } from 'framer-motion';
import { useRef } from 'react';
import GlassCard from '../../components/GlassCard';
import OrganizationProps from '../../model/organization-props';
import BusinessIcon from '@mui/icons-material/Business';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import LocationOnIcon from '@mui/icons-material/LocationOn';

const organizations = [
  {
    name: 'Amdocs',
    role: 'Senior Software Engineer',
    period: '2022 - Present',
    location: 'Gurugram, India',
    description: 'Working in the Network Automation unit, architecting and implementing microservices ecosystem processing 100,000+ network configurations daily with 99.9% reliability. Building high-performance data transformation pipelines and real-time anomaly detection systems for telecom infrastructure.',
    highlights: [
      'Backend Development',
      'System Architecture',
      'Threat Mitigation',
      'Microservices',
    ],
  },
  {
    name: 'Solitera Software',
    role: 'Full Stack Developer',
    period: '2019 - 2022',
    location: 'Gurugram, India',
    description: 'Led development of multiple projects including mental wellness apps, social gaming platforms, and enterprise management systems using Flutter, Firebase, and Node.js.',
    highlights: [
      'Full Stack Development',
      'Team Leadership',
      'Mobile Development',
      'API Design',
    ],
  },
];

export default function Organizations({ isMobile }: OrganizationProps) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });

  return (
    <Box
      component="section"
      id="organizations"
      ref={ref}
      sx={{
        py: { xs: 10, md: 15 },
        position: 'relative',
      }}
    >
      <Container maxWidth="lg">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
        >
          <Typography
            sx={{
              fontFamily: 'Fira Code, monospace',
              fontSize: '0.85rem',
              color: '#44D482',
              letterSpacing: '0.1em',
              mb: 2,
            }}
          >
            {'// EXPERIENCE'}
          </Typography>
          <Typography
            variant="h2"
            sx={{
              fontFamily: 'Bebas Neue, sans-serif',
              fontSize: { xs: '2.5rem', md: '4rem' },
              color: '#fff',
              mb: 6,
            }}
          >
            ORGANIZATIONS
          </Typography>
        </motion.div>

        {/* Organizations Grid */}
        {organizations.map((org, index) => (
          <motion.div
            key={org.name}
            initial={{ opacity: 0, y: 40 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.2 + index * 0.15 }}
          >
            <GlassCard
              enableTilt={!isMobile}
              sx={{
                mb: 4,
                overflow: 'visible',
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: { xs: 'column', md: 'row' },
                  gap: 4,
                }}
              >
                {/* Company Icon/Logo Area */}
                <Box
                  sx={{
                    width: { xs: '100%', md: '180px' },
                    minHeight: '140px',
                    background: 'linear-gradient(135deg, rgba(68, 212, 130, 0.1) 0%, rgba(68, 212, 130, 0.05) 100%)',
                    borderRadius: '16px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    border: '1px solid rgba(68, 212, 130, 0.15)',
                    flexShrink: 0,
                  }}
                >
                  <motion.div
                    animate={{
                      rotate: [0, 5, -5, 0],
                    }}
                    transition={{
                      duration: 6,
                      repeat: Infinity,
                      ease: 'easeInOut',
                    }}
                  >
                    <BusinessIcon
                      sx={{
                        fontSize: 70,
                        color: '#44D482',
                        opacity: 0.8,
                      }}
                    />
                  </motion.div>
                </Box>

                {/* Content */}
                <Box sx={{ flex: 1 }}>
                  <Typography
                    variant="h4"
                    sx={{
                      fontFamily: 'Bebas Neue, sans-serif',
                      fontSize: { xs: '1.8rem', md: '2.2rem' },
                      color: '#fff',
                      mb: 0.5,
                    }}
                  >
                    {org.name}
                  </Typography>

                  <Typography
                    sx={{
                      color: '#44D482',
                      fontWeight: 500,
                      fontSize: '1.1rem',
                      mb: 2,
                    }}
                  >
                    {org.role}
                  </Typography>

                  <Box
                    sx={{
                      display: 'flex',
                      gap: 3,
                      mb: 2,
                      flexWrap: 'wrap',
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <CalendarTodayIcon sx={{ fontSize: 16, color: 'rgba(255,255,255,0.5)' }} />
                      <Typography
                        sx={{
                          fontFamily: 'Fira Code, monospace',
                          fontSize: '0.8rem',
                          color: 'rgba(255, 255, 255, 0.6)',
                        }}
                      >
                        {org.period}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <LocationOnIcon sx={{ fontSize: 16, color: 'rgba(255,255,255,0.5)' }} />
                      <Typography
                        sx={{
                          fontFamily: 'Fira Code, monospace',
                          fontSize: '0.8rem',
                          color: 'rgba(255, 255, 255, 0.6)',
                        }}
                      >
                        {org.location}
                      </Typography>
                    </Box>
                  </Box>

                  <Typography
                    sx={{
                      color: 'rgba(255, 255, 255, 0.7)',
                      fontSize: '0.95rem',
                      lineHeight: 1.7,
                      mb: 3,
                    }}
                  >
                    {org.description}
                  </Typography>

                  {/* Highlights */}
                  <Box
                    sx={{
                      display: 'flex',
                      flexWrap: 'wrap',
                      gap: 1.5,
                    }}
                  >
                    {org.highlights.map((highlight) => (
                      <Box
                        key={highlight}
                        sx={{
                          padding: '6px 14px',
                          background: 'rgba(68, 212, 130, 0.1)',
                          border: '1px solid rgba(68, 212, 130, 0.2)',
                          borderRadius: '20px',
                          fontFamily: 'Fira Code, monospace',
                          fontSize: '0.75rem',
                          color: '#44D482',
                        }}
                      >
                        {highlight}
                      </Box>
                    ))}
                  </Box>
                </Box>
              </Box>
            </GlassCard>
          </motion.div>
        ))}
      </Container>
    </Box>
  );
}
